import type { QueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { GET_ASSET_QUERY_KEY } from '../api/assets/useGetAsset';
import { ASSET_POLL_INTERVAL_MS } from './pollingConstants';

const BATCH_CHUNK_SIZE = 500;

export class RefetchManager {
  private static instance: RefetchManager | undefined;
  private readonly subscribers = new Set<string>();
  private interval: ReturnType<typeof setInterval> | undefined;
  private tenantId: string | undefined;
  private queryClient: QueryClient | undefined;

  static getInstance(): RefetchManager {
    RefetchManager.instance = RefetchManager.instance ?? new RefetchManager();
    return RefetchManager.instance;
  }

  configure(context: { tenantId: string | undefined; queryClient: QueryClient }): void {
    if (this.tenantId !== undefined && context.tenantId !== this.tenantId) {
      this.subscribers.clear();
    }
    this.tenantId = context.tenantId;
    this.queryClient = context.queryClient;
  }

  register(assetId: string): void {
    if (this.subscribers.has(assetId)) return;
    this.subscribers.add(assetId);
    if (!this.interval) {
      setTimeout(() => void this.tick(), 0);
      this.interval = setInterval(() => void this.tick(), ASSET_POLL_INTERVAL_MS);
    }
  }

  unregister(assetId: string): void {
    if (!this.subscribers.delete(assetId)) return;
    if (this.subscribers.size === 0 && this.interval) {
      clearInterval(this.interval);
      this.interval = undefined;
    }
  }

  private async tick(): Promise<void> {
    const tenantId = this.tenantId;
    const queryClient = this.queryClient;
    if (this.subscribers.size === 0 || !tenantId || !queryClient) return;

    const allAssetIds = [...this.subscribers];
    for (let offset = 0; offset < allAssetIds.length; offset += BATCH_CHUNK_SIZE) {
      const assetIds = allAssetIds.slice(offset, offset + BATCH_CHUNK_SIZE);
      const response = await Promise.resolve(api.assets.batchGetAssets(tenantId, { assetIds })).catch(() => {});
      if (!response) continue;
      for (const asset of response.data.assets) {
        queryClient.setQueryData([GET_ASSET_QUERY_KEY, { assetId: asset.id, tenantId: asset.tenantId }], {
          data: { asset },
        });
      }
    }
  }
}
